import { hashKey, Query } from '@tanstack/react-query';
import { memoize } from 'lodash-es';
const DO_NOT_CACHE = 'do_not_cache';
const makeQueryKey = memoize((...args) => args, (...args) => hashKey(args));
/**
 * Static helper class for QueryClient configuration.
 */
export class QueryClientHelper {
    static VERSION = '';
    static GC_TIME = 1e3 * 60 * 5; // 5 minutes is the ReactQuery default
    static STALE_TIME = Infinity; // All data is fresh until explicitly invalidated
    /**
     * Sets the version of the query client.
     * @param version The version of the query client
     */
    static setVersion(version) {
        if (QueryClientHelper.VERSION)
            return;
        QueryClientHelper.VERSION = version;
    }
    /**
     * Makes a memoized query key.
     * @param args The query key arguments
     * @returns The query key array
     */
    static makeQueryKey(...args) {
        return makeQueryKey(QueryClientHelper.VERSION, ...args);
    }
    /**
     * Makes a memoized query key for ephemeral data (i.e. not persisted to cache).
     * @param args The query key arguments
     * @returns The query key array
     */
    static makeEphemeralQueryKey(...args) {
        return makeQueryKey(DO_NOT_CACHE, ...args);
    }
    /**
     * Checks if a query key is for ephemeral data (i.e. not persisted to cache).
     * @param queryKey The query key array
     * @returns True if the query key is for ephemeral data, false otherwise
     */
    static isEphemeralQueryKey(queryKey) {
        return queryKey.at(0) === DO_NOT_CACHE;
    }
    /**
     * Checks if a query key is a versioned query key (i.e. persisted with a version).
     * @param queryKey The query key array
     * @returns True if the query key is a versioned query key, false otherwise
     */
    static isVersionedQueryKey(queryKey) {
        return queryKey.at(0) === QueryClientHelper.VERSION;
    }
    /**
     * Unmakes a query key by removing the version or ephemeral flag if present.
     * @param queryKey The query key array
     * @returns The unmade query key array
     */
    static unmakeQueryKey(queryKey) {
        return this.isVersionedQueryKey(queryKey) || this.isEphemeralQueryKey(queryKey)
            ? queryKey.slice(1)
            : queryKey;
    }
    /**
     * A default function for cacluclating the delay of a QueryClient
     * query or mutation. Can be overridden at the `useQuery` or `useMutation`
     * call site. If `retryQuery` does not permit a retry, the return value is
     * ignored.
     * @param attempt The current number of attempts
     * @param err The error
     * @returns Number to delay time in milliseconds
     */
    static retryDelay(attempt, _err) {
        // TODO: We should refactor all data fetching to a more consistent error object
        // that when thrown includes response headers and http status so that we can use
        // a retryAfter and/or retry logic for the status code
        return Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000);
    }
    /**
     * A default function for determining if a query should be retried. Can be
     * overridden at the `useQuery` call site.
     *
     * Note: Retries are currently handled in the `authedHttpFetch` function, so this
     * function return false for now.
     *
     * @param attempt The current number of attempts
     * @param err The error
     * @returns Boolean to retry or not
     */
    static retryQuery(_attempt, _err) {
        // TODO: We should refactor all data fetching to a more consistent error object
        // that when thrown includes http status so that we can determine if we should
        // retry or not, since some status codes are not actionable and a retry would be
        // futile
        return false;
    }
    /**
     * A default function for determining if a mutation should be retried. Can be
     * overridden at the `useMutation` call site.
     *
     * Note: Retries are currently handled in the `authedHttpFetch` function, so this
     * function return false for now.
     *
     * @param attempt The current number of attempts
     * @param _err The error
     * @returns Boolean to retry or not
     */
    static retryMutation(_attempt, _err) {
        // TODO: We should refactor all data fetching to a more consistent error object
        // that when thrown includes http status so that we can determine if we should
        // retry or not, since some status codes are not actionable and a retry would be
        // futile
        return false;
    }
    /**
     * Given a list of query keys, returns a predicate fn that matches those queries.
     * Useful for invalidating queries.
     *
     * Ex:
     * queryClient.invalidateQueries({
     *   predicate: QueryClientHelper.predicateForKeys([
     *     CONNECTOR_FILES_QUERY_KEY,
     *     CONNECTOR_FILES_INFINITE_QUERY_KEY,
     *     CONNECTOR_HAS_UPLOADED_FILES_QUERY_KEY,
     *   ]),
     * });
     *
     * @param keys List of query keys to match against
     * @returns Predicate fn useful for invalidating queries, etc
     */
    static predicateForKeys(keys) {
        return ({ queryKey }) => {
            const [key = ''] = QueryClientHelper.unmakeQueryKey(queryKey);
            return keys.includes(key);
        };
    }
}
//# sourceMappingURL=QueryClientHelper.js.map